import React, { useState, useEffect, useCallback } from 'react';
import type { RecWeightSet } from '@musedrops/shared';
import { logger } from '@musedrops/shared';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Plus, Copy } from 'lucide-react';
import {
  listRecWeightSets,
  createRecWeightSet,
  updateRecWeightSet,
  setDefaultWeightSet,
} from '../../../data/rec-weight-set';

/** Field metadata for the two-column layout */
interface FieldConfig {
  key: keyof RecWeightSet;
  group: string;
  description: string | ((val: number) => string);
  min: number;
  max: number;
  step: number;
}

const NUMERIC_FIELDS: FieldConfig[] = [
  // ── Embedding ──
  {
    key: 'alphaShort',
    group: 'Embedding',
    description: (v: number) =>
      `Short-term effect (EMA rate). Events blend ${Math.round(v * 100)}% new signal, ${Math.round((1 - v) * 100)}% old`,
    min: 0,
    max: 1,
    step: 0.01,
  },
  {
    key: 'alphaLong',
    group: 'Embedding',
    description: (v: number) =>
      `Long-term effect (EMA rate). Events blend ${Math.round(v * 100)}% new signal, ${Math.round((1 - v) * 100)}% old (daily batch)`,
    min: 0,
    max: 1,
    step: 0.01,
  },
  {
    key: 'lambda',
    group: 'Embedding',
    description: (v: number) =>
      `Effective = ${v} * short + ${+(1 - v).toFixed(2)} * long`,
    min: 0,
    max: 1,
    step: 0.01,
  },
  {
    key: 'shortTermExpiryDays',
    group: 'Embedding',
    description: (v: number) =>
      `Short-term embedding expires after ${v} day${v !== 1 ? 's' : ''} of inactivity`,
    min: 1,
    max: 365,
    step: 1,
  },
  {
    key: 'fullListenRatio',
    group: 'Embedding',
    description: (v: number) =>
      `Skip signal ramps from -1 at 0% to +1 at ${Math.round(v * 100)}% or higher (neutral at ${Math.round(v * 50)}%)`,
    min: 0.1,
    max: 1,
    step: 0.05,
  },
  {
    key: 'inactivityTimeoutMin',
    group: 'Embedding',
    description: (v: number) =>
      `Complete events are "active" if user interacted within ${v} min, "passive" (weaker signal) otherwise`,
    min: 1,
    max: 120,
    step: 1,
  },
  // ── Cooldown & Re-listen ──
  {
    key: 'cooldownMinPositionSec',
    group: 'Cooldown & Re-listen',
    description: (v: number) =>
      `Stories heard < ${v}s can reappear after cooldown. Others are permanently excluded`,
    min: 0,
    max: 300,
    step: 1,
  },
  {
    key: 'cooldownMaxTimesListened',
    group: 'Cooldown & Re-listen',
    description: (v: number) =>
      `Stories heard < ${v} time${v !== 1 ? 's' : ''} can reappear. Others are permanently excluded`,
    min: 1,
    max: 10,
    step: 1,
  },
  {
    key: 'cooldownDays',
    group: 'Cooldown & Re-listen',
    description: (v: number) =>
      `Barely-heard stories won't reappear for ${v} day${v !== 1 ? 's' : ''} (fully-listened stories are always excluded)`,
    min: 0,
    max: 365,
    step: 1,
  },
  {
    key: 'repeatPenaltyMax',
    group: 'Cooldown & Re-listen',
    description: (v: number) =>
      `Up to -${v} score penalty for re-listened stories`,
    min: 0,
    max: 5,
    step: 0.01,
  },
  {
    key: 'repeatDecayDays',
    group: 'Cooldown & Re-listen',
    description: (v: number) =>
      `Repeat penalty fades over ${v} day${v !== 1 ? 's' : ''}`,
    min: 0,
    max: 365,
    step: 1,
  },
  // ── Scoring ──
  {
    key: 'followBoost',
    group: 'Scoring',
    description: (v: number) =>
      `+${v} score bonus for stories from followed shows`,
    min: 0,
    max: 5,
    step: 0.01,
  },
  {
    key: 'freshnessBoostMax',
    group: 'Scoring',
    description: (v: number) => `Up to +${v} score bonus for brand-new stories`,
    min: 0,
    max: 5,
    step: 0.01,
  },
  {
    key: 'freshnessDecayDays',
    group: 'Scoring',
    description: (v: number) =>
      `Freshness bonus decays to zero over ${v} day${v !== 1 ? 's' : ''}`,
    min: 0,
    max: 365,
    step: 1,
  },
  // ── Gap Enforcement ──
  {
    key: 'sameClusterGap',
    group: 'Gap Enforcement',
    description: (v: number) =>
      `Min ${v} position${v !== 1 ? 's' : ''} between stories from the same content cluster`,
    min: 0,
    max: 20,
    step: 1,
  },
  {
    key: 'sameShowGap',
    group: 'Gap Enforcement',
    description: (v: number) =>
      `Min ${v} position${v !== 1 ? 's' : ''} between stories from the same show`,
    min: 0,
    max: 20,
    step: 1,
  },
  // ── Diversity & Exploration (legacy) ──
  {
    key: 'maxPerShowTop10',
    group: 'Diversity & Exploration',
    description: 'Legacy — replaced by sameShowGap',
    min: 1,
    max: 10,
    step: 1,
  },
  {
    key: 'explorationRate',
    group: 'Diversity & Exploration',
    description: (v: number) =>
      `${Math.round(v * 100)}% — legacy, no longer used by pipeline`,
    min: 0,
    max: 1,
    step: 0.01,
  },
  // ── Retrieval ──
  {
    key: 'candidatePoolSize',
    group: 'Retrieval',
    description: 'Number of candidate stories retrieved from pgvector',
    min: 10,
    max: 5000,
    step: 10,
  },
];

function getFieldDescription(field: FieldConfig, value: number): string {
  return typeof field.description === 'function'
    ? field.description(value)
    : field.description;
}

function validateField(field: FieldConfig, value: number): string | null {
  if (Number.isNaN(value)) return `${field.key} must be a number`;
  if (value < field.min) return `${field.key} must be >= ${field.min}`;
  if (value > field.max) return `${field.key} must be <= ${field.max}`;
  return null;
}

interface DialogState {
  open: boolean;
  mode: 'create' | 'edit';
  id: string;
  isDefault: boolean;
  values: Record<string, number>;
  eventWeightsText: string;
}

const DEFAULT_VALUES: Record<string, number> = {
  alphaShort: 0.15,
  alphaLong: 0.03,
  lambda: 0.6,
  cooldownDays: 14,
  explorationRate: 0.12,
  followBoost: 0.1,
  freshnessBoostMax: 0.05,
  freshnessDecayDays: 30,
  repeatPenaltyMax: 0.15,
  repeatDecayDays: 90,
  maxPerShowTop10: 2,
  sameClusterGap: 3,
  sameShowGap: 6,
  candidatePoolSize: 300,
  shortTermExpiryDays: 7,
  cooldownMinPositionSec: 10,
  cooldownMaxTimesListened: 2,
  fullListenRatio: 0.6,
  inactivityTimeoutMin: 15,
};

const emptyDialog: DialogState = {
  open: false,
  mode: 'create',
  id: '',
  isDefault: false,
  values: { ...DEFAULT_VALUES },
  eventWeightsText: JSON.stringify(
    {
      start: 0.05,
      save: 0.8,
      unsave: -0.2,
      rewind: 0.6,
      fast_forward: -0.1,
      skip_scale: 0.4,
      complete_active: 1.0,
      complete_passive: 0.6,
    },
    null,
    2
  ),
};

export const RecWeightsSettings: React.FC = () => {
  const [weightSets, setWeightSets] = useState<RecWeightSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [dialog, setDialog] = useState<DialogState>(emptyDialog);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listRecWeightSets();
      setWeightSets(data);
    } catch (err) {
      logger.error('Failed to load weight sets', { error: String(err) });
      setNotification({ message: 'Failed to load weight sets', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Dialog ──

  const openCreateDialog = () => {
    setDialog({ ...emptyDialog, open: true, mode: 'create' });
    setDialogError(null);
  };

  const openEditDialog = (ws: RecWeightSet) => {
    const values: Record<string, number> = {};
    for (const field of NUMERIC_FIELDS) {
      values[field.key] = ws[field.key] as number;
    }

    setDialog({
      open: true,
      mode: 'edit',
      id: ws.id,
      isDefault: ws.isDefault,
      values,
      eventWeightsText: JSON.stringify(ws.eventWeights, null, 2),
    });
    setDialogError(null);
  };

  const openDuplicateDialog = (ws: RecWeightSet) => {
    const values: Record<string, number> = {};
    for (const field of NUMERIC_FIELDS) {
      values[field.key] = ws[field.key] as number;
    }

    setDialog({
      open: true,
      mode: 'create',
      id: `${ws.id}-copy`,
      isDefault: false,
      values,
      eventWeightsText: JSON.stringify(ws.eventWeights, null, 2),
    });
    setDialogError(null);
  };

  const closeDialog = () => {
    setDialog(emptyDialog);
    setDialogError(null);
  };

  const handleDialogSave = async () => {
    // Validate id
    if (!dialog.id.trim()) {
      setDialogError('ID is required');
      return;
    }

    // Validate numeric fields
    for (const field of NUMERIC_FIELDS) {
      const err = validateField(field, dialog.values[field.key] ?? 0);
      if (err) {
        setDialogError(err);
        return;
      }
    }

    // Validate eventWeights JSON
    let parsedEventWeights: Record<string, number>;
    try {
      parsedEventWeights = JSON.parse(dialog.eventWeightsText);
      if (
        typeof parsedEventWeights !== 'object' ||
        parsedEventWeights === null ||
        Array.isArray(parsedEventWeights)
      ) {
        throw new Error('Must be a JSON object');
      }
    } catch (err) {
      setDialogError(
        `Invalid eventWeights JSON: ${err instanceof Error ? err.message : String(err)}`
      );
      return;
    }

    try {
      setSaving(true);
      const payload: Record<string, unknown> = {
        ...dialog.values,
        eventWeights: parsedEventWeights,
      };

      if (dialog.mode === 'create') {
        await createRecWeightSet({
          id: dialog.id,
          ...payload,
        } as Partial<RecWeightSet> & { id: string });
        setNotification({
          message: `Weight set "${dialog.id}" created`,
          type: 'success',
        });
      } else {
        await updateRecWeightSet(dialog.id, payload as Partial<RecWeightSet>);
        setNotification({
          message: `Weight set "${dialog.id}" updated`,
          type: 'success',
        });
      }
      closeDialog();
      await loadData();
    } catch (err) {
      logger.error('Failed to save weight set', { error: String(err) });
      setDialogError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async () => {
    try {
      setSaving(true);
      await setDefaultWeightSet(dialog.id);
      setDialog(d => ({ ...d, isDefault: true }));
      setNotification({
        message: `"${dialog.id}" is now the default weight set`,
        type: 'success',
      });
      await loadData();
    } catch (err) {
      logger.error('Failed to set default', { error: String(err) });
      setDialogError(err instanceof Error ? err.message : 'Failed to set default');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">
                <IconButton
                  onClick={openCreateDialog}
                  color="primary"
                  size="small"
                >
                  <Plus size={18} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {weightSets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No weight sets yet. Click + to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              weightSets.map(ws => (
                <TableRow
                  key={ws.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openEditDialog(ws)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {ws.id}
                      </Typography>
                      {ws.isDefault && (
                        <Chip label="default" size="small" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(ws.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" onClick={e => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      onClick={() => openDuplicateDialog(ws)}
                    >
                      <Copy size={14} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {dialog.mode === 'create'
            ? 'Create Weight Set'
            : `Edit: ${dialog.id}`}
        </DialogTitle>
        <DialogContent>
          {/* ID field */}
          <TextField
            label="ID"
            value={dialog.id}
            onChange={e => setDialog(d => ({ ...d, id: e.target.value }))}
            fullWidth
            margin="normal"
            disabled={dialog.mode === 'edit'}
            helperText={
              dialog.mode === 'create' ? 'e.g. v2, experimental' : undefined
            }
          />

          {/* Numeric fields grouped by category */}
          <Box
            sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            {NUMERIC_FIELDS.map((field, idx) => {
              const val = dialog.values[field.key] ?? 0;
              const error = validateField(field, val);
              const prevGroup = idx > 0 ? NUMERIC_FIELDS[idx - 1]?.group : undefined;
              const showHeader = idx === 0 || field.group !== prevGroup;
              return (
                <React.Fragment key={field.key}>
                  {showHeader && (
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mt: idx === 0 ? 0 : 2, mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}
                    >
                      {field.group}
                    </Typography>
                  )}
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    <TextField
                      label={field.key}
                      type="number"
                      value={dialog.values[field.key] ?? ''}
                      onChange={e => {
                        const v =
                          e.target.value === '' ? 0 : Number(e.target.value);
                        setDialog(d => ({
                          ...d,
                          values: { ...d.values, [field.key]: v },
                        }));
                      }}
                      error={!!error}
                      size="small"
                      sx={{ width: 160, flexShrink: 0 }}
                      inputProps={{
                        min: field.min,
                        max: field.max,
                        step: field.step,
                      }}
                    />
                    <Typography
                      variant="body2"
                      color={error ? 'error' : 'text.secondary'}
                    >
                      {error ?? getFieldDescription(field, val)}
                    </Typography>
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>

          {/* eventWeights JSON field */}
          <TextField
            label="eventWeights"
            value={dialog.eventWeightsText}
            onChange={e =>
              setDialog(d => ({ ...d, eventWeightsText: e.target.value }))
            }
            fullWidth
            multiline
            rows={10}
            margin="normal"
            sx={{ fontFamily: 'monospace', mt: 3 }}
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            JSON map of event actions to signal weights. Keys: start, save,
            unsave, rewind, fast_forward, skip_scale, complete_active,
            complete_passive
          </Typography>

          {dialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {dialog.mode === 'edit' && !dialog.isDefault && (
              <Button onClick={handleSetDefault} disabled={saving}>
                Set as Default
              </Button>
            )}
            {dialog.mode === 'edit' && dialog.isDefault && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                Default
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleDialogSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={16} /> : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.type}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
